import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const RecurringBilling = () => {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [subInvoices, setSubInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubModal, setShowSubModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [subForm, setSubForm] = useState({
    customer_id: '', plan_id: '', billing_cycle: 'monthly', amount: '',
    start_date: new Date()?.toISOString()?.split('T')?.[0], notes: '', auto_generate_invoice: true
  });
  const [planForm, setPlanForm] = useState({
    name: '', description: '', billing_cycle: 'monthly', amount: '', currency: 'USD', tax_rate: 0
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [subsRes, plansRes, custRes, siRes] = await Promise.all([
        supabase?.from('customer_subscriptions')?.select('*, customer:customers(name, email), plan:recurring_billing_plans(name, billing_cycle)')?.order('created_at', { ascending: false }),
        supabase?.from('recurring_billing_plans')?.select('*')?.order('created_at'),
        supabase?.from('customers')?.select('id, name, email')?.eq('status', 'active')?.order('name'),
        supabase?.from('subscription_invoices')?.select('*, subscription:customer_subscriptions(*, customer:customers(name)), invoice:invoices(invoice_number, invoice_status)')?.order('generated_at', { ascending: false })?.limit(50)
      ]);
      setSubscriptions(subsRes?.data || []);
      setPlans(plansRes?.data || []);
      setCustomers(custRes?.data || []);
      setSubInvoices(siRes?.data || []);
    } catch (err) {
      console.error('Error fetching recurring billing data:', err?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const calcNextBillingDate = (startDate, cycle) => {
    const d = new Date(startDate);
    if (cycle === 'weekly') d?.setDate(d?.getDate() + 7);
    else if (cycle === 'monthly') d?.setMonth(d?.getMonth() + 1);
    else if (cycle === 'quarterly') d?.setMonth(d?.getMonth() + 3);
    else if (cycle === 'annually') d?.setFullYear(d?.getFullYear() + 1);
    return d?.toISOString();
  };

  const handleSaveSubscription = async () => {
    try {
      const nextBilling = calcNextBillingDate(subForm?.start_date, subForm?.billing_cycle);
      const payload = {
        customer_id: subForm?.customer_id,
        plan_id: subForm?.plan_id || null,
        billing_cycle: subForm?.billing_cycle,
        amount: parseFloat(subForm?.amount),
        start_date: subForm?.start_date,
        next_billing_date: nextBilling,
        notes: subForm?.notes,
        auto_generate_invoice: subForm?.auto_generate_invoice,
        status: 'active',
        updated_at: new Date()?.toISOString()
      };
      if (editingSub?.id) {
        await supabase?.from('customer_subscriptions')?.update(payload)?.eq('id', editingSub?.id);
      } else {
        await supabase?.from('customer_subscriptions')?.insert([payload]);
      }
      setShowSubModal(false);
      setEditingSub(null);
      setSubForm({ customer_id: '', plan_id: '', billing_cycle: 'monthly', amount: '', start_date: new Date()?.toISOString()?.split('T')?.[0], notes: '', auto_generate_invoice: true });
      showSuccess('Subscription saved successfully');
      fetchData();
    } catch (err) {
      console.error('Error saving subscription:', err?.message);
    }
  };

  const handleSavePlan = async () => {
    try {
      const payload = { ...planForm, amount: parseFloat(planForm?.amount), tax_rate: parseFloat(planForm?.tax_rate || 0), updated_at: new Date()?.toISOString() };
      if (editingPlan?.id) {
        await supabase?.from('recurring_billing_plans')?.update(payload)?.eq('id', editingPlan?.id);
      } else {
        await supabase?.from('recurring_billing_plans')?.insert([payload]);
      }
      setShowPlanModal(false);
      setEditingPlan(null);
      setPlanForm({ name: '', description: '', billing_cycle: 'monthly', amount: '', currency: 'USD', tax_rate: 0 });
      showSuccess('Plan saved successfully');
      fetchData();
    } catch (err) {
      console.error('Error saving plan:', err?.message);
    }
  };

  const handleCancelSubscription = async (sub) => {
    if (!window.confirm(`Cancel subscription for ${sub?.customer?.name}?`)) return;
    await supabase?.from('customer_subscriptions')?.update({ status: 'cancelled', end_date: new Date()?.toISOString(), updated_at: new Date()?.toISOString() })?.eq('id', sub?.id);
    showSuccess('Subscription cancelled');
    fetchData();
  };

  const handleGenerateInvoice = async (sub) => {
    try {
      const now = new Date();
      const periodEnd = new Date(calcNextBillingDate(now.toISOString(), sub?.billing_cycle));
      // Create invoice
      const { data: inv, error: invErr } = await supabase?.from('invoices')?.insert([{
        customer_id: sub?.customer_id,
        invoice_status: 'draft',
        subtotal: sub?.amount,
        total_amount: sub?.amount,
        amount_due: sub?.amount,
        due_date: periodEnd?.toISOString(),
        notes: `Recurring billing - ${sub?.billing_cycle} subscription`
      }])?.select()?.single();
      if (invErr) throw invErr;
      // Create subscription invoice record
      await supabase?.from('subscription_invoices')?.insert([{
        subscription_id: sub?.id,
        invoice_id: inv?.id,
        billing_period_start: now?.toISOString(),
        billing_period_end: periodEnd?.toISOString(),
        amount: sub?.amount,
        status: 'generated'
      }]);
      // Update last billed date
      await supabase?.from('customer_subscriptions')?.update({ last_billed_date: now?.toISOString(), next_billing_date: periodEnd?.toISOString(), updated_at: now?.toISOString() })?.eq('id', sub?.id);
      showSuccess(`Invoice generated for ${sub?.customer?.name}`);
      fetchData();
    } catch (err) {
      console.error('Error generating invoice:', err?.message);
    }
  };

  const handleBulkGenerate = async () => {
    const due = subscriptions?.filter(s => s?.status === 'active' && s?.auto_generate_invoice && s?.next_billing_date && new Date(s?.next_billing_date) <= new Date());
    if (due?.length === 0) { showSuccess('No subscriptions due for billing'); return; }
    for (const sub of due) { await handleGenerateInvoice(sub); }
    showSuccess(`Generated invoices for ${due?.length} subscriptions`);
  };

  const filteredSubs = subscriptions?.filter(s => statusFilter === 'all' || s?.status === statusFilter);

  const getStatusBadge = (status) => {
    const map = { active: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', paused: 'bg-yellow-100 text-yellow-800', expired: 'bg-gray-100 text-gray-600' };
    return map?.[status] || 'bg-gray-100 text-gray-600';
  };

  const formatDate = (d) => d ? new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
  const formatCurrency = (v) => `$${parseFloat(v || 0)?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

  const stats = {
    active: subscriptions?.filter(s => s?.status === 'active')?.length,
    mrr: subscriptions?.filter(s => s?.status === 'active')?.reduce((sum, s) => {
      const amt = parseFloat(s?.amount || 0);
      if (s?.billing_cycle === 'annually') return sum + amt / 12;
      if (s?.billing_cycle === 'quarterly') return sum + amt / 3;
      if (s?.billing_cycle === 'weekly') return sum + amt * 4.33;
      return sum + amt;
    }, 0),
    dueSoon: subscriptions?.filter(s => s?.status === 'active' && s?.next_billing_date && new Date(s?.next_billing_date) <= new Date(Date.now() + 7 * 86400000))?.length
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Recurring Billing | FieldServi</title></Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Subscription & Recurring Billing</h1>
            <p className="text-muted-foreground mt-1">Manage customer subscriptions and automate recurring invoice generation</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleBulkGenerate}>
              <Icon name="Zap" size={16} className="mr-2" />Generate Due Invoices
            </Button>
            <Button onClick={() => { setEditingSub(null); setSubForm({ customer_id: '', plan_id: '', billing_cycle: 'monthly', amount: '', start_date: new Date()?.toISOString()?.split('T')?.[0], notes: '', auto_generate_invoice: true }); setShowSubModal(true); }}>
              <Icon name="Plus" size={16} className="mr-2" />New Subscription
            </Button>
          </div>
        </div>

        {successMsg && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm flex items-center gap-2">
            <Icon name="CheckCircle" size={16} />{successMsg}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: 'Active Subscriptions', value: stats?.active, icon: 'RefreshCw', color: 'text-green-600 bg-green-50' },
            { label: 'Monthly Recurring Revenue', value: formatCurrency(stats?.mrr), icon: 'TrendingUp', color: 'text-blue-600 bg-blue-50' },
            { label: 'Due This Week', value: stats?.dueSoon, icon: 'Calendar', color: 'text-orange-600 bg-orange-50' }
          ]?.map(stat => (
            <div key={stat?.label} className="bg-card border border-border rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${stat?.color}`}><Icon name={stat?.icon} size={18} /></div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
                  <p className="text-xs text-muted-foreground">{stat?.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          {[
            { id: 'subscriptions', label: 'Subscriptions', icon: 'RefreshCw' },
            { id: 'plans', label: 'Billing Plans', icon: 'Package' },
            { id: 'history', label: 'Invoice History', icon: 'FileText' }
          ]?.map(tab => (
            <button key={tab?.id} onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}>
              <Icon name={tab?.icon} size={16} />{tab?.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
        ) : activeTab === 'subscriptions' ? (
          <div>
            <div className="flex gap-2 mb-4 flex-wrap">
              {['all', 'active', 'paused', 'cancelled']?.map(f => (
                <button key={f} onClick={() => setStatusFilter(f)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize transition-colors ${
                    statusFilter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}>{f === 'all' ? `All (${subscriptions?.length})` : `${f} (${subscriptions?.filter(s => s?.status === f)?.length})`}
                </button>
              ))}
            </div>
            {filteredSubs?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="RefreshCw" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No subscriptions found</p>
                <p className="text-sm mt-1">Create a subscription to start recurring billing</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Customer', 'Plan / Cycle', 'Amount', 'Next Billing', 'Status', 'Actions']?.map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filteredSubs?.map(sub => (
                      <tr key={sub?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="font-medium text-foreground">{sub?.customer?.name}</div>
                          <div className="text-xs text-muted-foreground">{sub?.customer?.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-foreground">{sub?.plan?.name || 'Custom'}</div>
                          <div className="text-xs text-muted-foreground capitalize">{sub?.billing_cycle}</div>
                        </td>
                        <td className="px-4 py-3 font-semibold text-foreground">{formatCurrency(sub?.amount)}</td>
                        <td className="px-4 py-3">
                          <div className={`text-sm ${sub?.next_billing_date && new Date(sub?.next_billing_date) <= new Date() ? 'text-red-600 font-medium' : 'text-foreground'}`}>
                            {formatDate(sub?.next_billing_date)}
                          </div>
                          {sub?.next_billing_date && new Date(sub?.next_billing_date) <= new Date() && (
                            <div className="text-xs text-red-500">Overdue</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(sub?.status)}`}>{sub?.status}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            {sub?.status === 'active' && (
                              <Button size="sm" variant="outline" onClick={() => handleGenerateInvoice(sub)} title="Generate Invoice">
                                <Icon name="FileText" size={12} className="mr-1" />Invoice
                              </Button>
                            )}
                            <button onClick={() => { setEditingSub(sub); setSubForm({ customer_id: sub?.customer_id, plan_id: sub?.plan_id || '', billing_cycle: sub?.billing_cycle, amount: sub?.amount, start_date: sub?.start_date?.split('T')?.[0], notes: sub?.notes || '', auto_generate_invoice: sub?.auto_generate_invoice }); setShowSubModal(true); }} className="p-1.5 text-muted-foreground hover:text-foreground rounded border border-border">
                              <Icon name="Edit" size={12} />
                            </button>
                            {sub?.status === 'active' && (
                              <button onClick={() => handleCancelSubscription(sub)} className="p-1.5 text-muted-foreground hover:text-red-500 rounded border border-border">
                                <Icon name="XCircle" size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeTab === 'plans' ? (
          <div>
            <div className="flex justify-end mb-4">
              <Button onClick={() => { setEditingPlan(null); setPlanForm({ name: '', description: '', billing_cycle: 'monthly', amount: '', currency: 'USD', tax_rate: 0 }); setShowPlanModal(true); }}>
                <Icon name="Plus" size={16} className="mr-2" />New Plan
              </Button>
            </div>
            {plans?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="Package" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No billing plans</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans?.map(plan => (
                  <div key={plan?.id} className="bg-card border border-border rounded-xl p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold text-foreground">{plan?.name}</h3>
                        <p className="text-sm text-muted-foreground capitalize">{plan?.billing_cycle}</p>
                      </div>
                      <button onClick={() => { setEditingPlan(plan); setPlanForm({ name: plan?.name, description: plan?.description || '', billing_cycle: plan?.billing_cycle, amount: plan?.amount, currency: plan?.currency, tax_rate: plan?.tax_rate || 0 }); setShowPlanModal(true); }} className="p-1.5 text-muted-foreground hover:text-foreground rounded">
                        <Icon name="Edit" size={14} />
                      </button>
                    </div>
                    <p className="text-3xl font-bold text-foreground mb-1">{formatCurrency(plan?.amount)}</p>
                    <p className="text-xs text-muted-foreground mb-3">per {plan?.billing_cycle?.replace('ly', '')}</p>
                    {plan?.description && <p className="text-sm text-muted-foreground">{plan?.description}</p>}
                    <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                      {subscriptions?.filter(s => s?.plan_id === plan?.id && s?.status === 'active')?.length} active subscribers
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            {subInvoices?.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No subscription invoices yet</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      {['Customer', 'Billing Period', 'Amount', 'Invoice', 'Status', 'Generated']?.map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {subInvoices?.map(si => (
                      <tr key={si?.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground">{si?.subscription?.customer?.name || 'N/A'}</td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(si?.billing_period_start)} – {formatDate(si?.billing_period_end)}</td>
                        <td className="px-4 py-3 font-semibold text-foreground">{formatCurrency(si?.amount)}</td>
                        <td className="px-4 py-3 text-foreground">{si?.invoice?.invoice_number || '—'}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">{si?.status}</span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDate(si?.generated_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
      {/* Subscription Modal */}
      {showSubModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card">
              <h2 className="text-lg font-semibold text-foreground">{editingSub?.id ? 'Edit Subscription' : 'New Subscription'}</h2>
              <button onClick={() => setShowSubModal(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Customer *</label>
                <select value={subForm?.customer_id} onChange={e => setSubForm(p => ({ ...p, customer_id: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                  <option value="">Select customer...</option>
                  {customers?.map(c => <option key={c?.id} value={c?.id}>{c?.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Billing Plan (optional)</label>
                <select value={subForm?.plan_id} onChange={e => {
                  const plan = plans?.find(p => p?.id === e?.target?.value);
                  setSubForm(p => ({ ...p, plan_id: e?.target?.value, amount: plan?.amount || p?.amount, billing_cycle: plan?.billing_cycle || p?.billing_cycle }));
                }} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                  <option value="">Custom / No plan</option>
                  {plans?.map(p => <option key={p?.id} value={p?.id}>{p?.name} — {formatCurrency(p?.amount)}/{p?.billing_cycle}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Billing Cycle</label>
                  <select value={subForm?.billing_cycle} onChange={e => setSubForm(p => ({ ...p, billing_cycle: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                    {['weekly', 'monthly', 'quarterly', 'annually']?.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Amount ($) *</label>
                  <input type="number" min={0} step={0.01} value={subForm?.amount} onChange={e => setSubForm(p => ({ ...p, amount: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
                <input type="date" value={subForm?.start_date} onChange={e => setSubForm(p => ({ ...p, start_date: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Notes</label>
                <textarea value={subForm?.notes} onChange={e => setSubForm(p => ({ ...p, notes: e?.target?.value }))} rows={2} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={subForm?.auto_generate_invoice} onChange={e => setSubForm(p => ({ ...p, auto_generate_invoice: e?.target?.checked }))} className="rounded" />
                <span className="text-sm text-foreground">Auto-generate invoice on billing date</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowSubModal(false)}>Cancel</Button>
              <Button onClick={handleSaveSubscription} disabled={!subForm?.customer_id || !subForm?.amount}>Save Subscription</Button>
            </div>
          </div>
        </div>
      )}
      {/* Plan Modal */}
      {showPlanModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h2 className="text-lg font-semibold text-foreground">{editingPlan?.id ? 'Edit Plan' : 'New Billing Plan'}</h2>
              <button onClick={() => setShowPlanModal(false)} className="text-muted-foreground hover:text-foreground"><Icon name="X" size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Plan Name *</label>
                <input value={planForm?.name} onChange={e => setPlanForm(p => ({ ...p, name: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="e.g. Monthly Maintenance" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                <textarea value={planForm?.description} onChange={e => setPlanForm(p => ({ ...p, description: e?.target?.value }))} rows={2} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Billing Cycle</label>
                  <select value={planForm?.billing_cycle} onChange={e => setPlanForm(p => ({ ...p, billing_cycle: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground">
                    {['weekly', 'monthly', 'quarterly', 'annually']?.map(c => <option key={c} value={c} className="capitalize">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Amount ($) *</label>
                  <input type="number" min={0} step={0.01} value={planForm?.amount} onChange={e => setPlanForm(p => ({ ...p, amount: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="0.00" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Tax Rate (%)</label>
                <input type="number" min={0} max={100} step={0.01} value={planForm?.tax_rate} onChange={e => setPlanForm(p => ({ ...p, tax_rate: e?.target?.value }))} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground" placeholder="0" />
              </div>
            </div>
            <div className="flex justify-end gap-2 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowPlanModal(false)}>Cancel</Button>
              <Button onClick={handleSavePlan} disabled={!planForm?.name || !planForm?.amount}>Save Plan</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurringBilling;
